import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { initAnalytics } from './utils/analytics';
import { CartProvider, useCart } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { PreviewProvider } from './context/PreviewContext';
import CartDrawer from './components/CartDrawer';
import Toast from './components/Toast';
import CookieConsent, { initConsentBasedAnalytics } from './components/CookieConsent';
import AutoPopup from './components/AutoPopup';

// Lazy load pages for better initial load performance
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Story = lazy(() => import('./pages/Story'));
const Contact = lazy(() => import('./pages/Contact'));
const BlogListing = lazy(() => import('./pages/BlogListing'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Account = lazy(() => import('./pages/Account'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const RefundReturns = lazy(() => import('./pages/RefundReturns'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const FAQ = lazy(() => import('./pages/FAQ'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-melanua-gold border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Component to enforce trailing slashes on URLs
function TrailingSlashRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Skip root path and paths that already have trailing slash
    if (location.pathname !== '/' && !location.pathname.endsWith('/')) {
      navigate(location.pathname + '/' + location.search + location.hash, { replace: true });
    }
  }, [location, navigate]);

  return null;
}

// Legacy toast notification container (for cart notifications)
function LegacyToastContainer() {
  const { notification } = useCart();

  if (!notification) return null;

  return (
    <Toast
      message={notification.message}
      type={notification.type}
    />
  );
}

function App() {
  // Initialize analytics on app load (only if consent given)
  useEffect(() => {
    initConsentBasedAnalytics();
    initAnalytics();
  }, []);

  return (
    <PreviewProvider>
      <AuthProvider>
        <CartProvider>
          <TrailingSlashRedirect />
          <CartDrawer />
          <AutoPopup />
          <ToastContainer
            position="top-right"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            toastClassName="font-sans"
          />
          <LegacyToastContainer />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products/" element={<Products />} />
            <Route path="/product/:slug/" element={<ProductDetail />} />
            <Route path="/story/" element={<Story />} />
            <Route path="/contact/" element={<Contact />} />
            <Route path="/blog/" element={<BlogListing />} />
            <Route path="/blog/:slug/" element={<BlogDetail />} />
            <Route path="/cart/" element={<Cart />} />
            <Route path="/checkout/" element={<Checkout />} />
            <Route path="/order-confirmation/:orderId/" element={<OrderConfirmation />} />
            <Route path="/login/" element={<Login />} />
            <Route path="/forgot-password/" element={<ForgotPassword />} />
            <Route path="/reset-password/:uid/:token/" element={<ResetPassword />} />
            <Route path="/account/" element={<Account />} />
            <Route path="/privacy-policy/" element={<PrivacyPolicy />} />
            <Route path="/terms/" element={<TermsConditions />} />
            <Route path="/refunds/" element={<RefundReturns />} />
            <Route path="/shipping/" element={<ShippingPolicy />} />
            <Route path="/faq/" element={<FAQ />} />
            {/* Also match without trailing slash for direct navigation */}
            <Route path="/products" element={<Products />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/story" element={<Story />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<BlogListing />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
            <Route path="/account" element={<Account />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/refunds" element={<RefundReturns />} />
            <Route path="/shipping" element={<ShippingPolicy />} />
            <Route path="/faq" element={<FAQ />} />
            {/* 404 catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
          <CookieConsent />
        </CartProvider>
      </AuthProvider>
    </PreviewProvider>
  );
}

export default App;
